import { useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  Fade,
  Snackbar,
  Stack,
  Typography,
  useMediaQuery
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import GppBadTwoToneIcon from '@mui/icons-material/GppBadTwoTone';
import { toast } from 'react-toastify';
import apiFetch from 'src/Common/api/auth/Auth';
import {
  apiErrorStatus,
  clearIpBlocked,
  dismissIpBlockedNotice,
  dismissRateLimitNotice,
  formatWait,
  isDuplicateOfAccessNotice,
  isIpBlockedError,
  useApiAccessState,
  useRateLimitCountdown
} from 'src/Common/api/apiAccessGuard';

/**
 * The app-wide answer to the API's two refusals (see src/Common/api/apiAccessGuard):
 *  - 429 rate limit → a "Too Many Requests" notice at the top with a live countdown;
 *  - 403 IP block   → an "Access Denied" dialog; "Try again" re-checks with ONE request.
 *
 * Mounted once (App.tsx). One notice per episode however many requests fail, and while it shows,
 * page toasts that merely repeat the refusal are dropped. It never signs anyone out, never retries
 * anything by itself and never touches a screen's state: carts and unsaved forms stay as they are.
 * Rendered with MUI rather than react-toastify so the countdown is never stuck behind the toast
 * container's limit / queue.
 */

const SUPPRESSED_TOAST_ID = 'api-access-duplicate';
const FILTERED_TOAST_TYPES = ['error', 'warning', 'warn', 'info'];

/** While mounted: a page toast that only repeats a refusal the open notice explains is dropped. */
function useDuplicateToastFilter(): void {
  useEffect(() => {
    const target = toast as unknown as Record<string, unknown>;
    const originals: Record<string, unknown> = {};
    FILTERED_TOAST_TYPES.forEach((type) => {
      const original = target[type];
      if (typeof original !== 'function') return;
      const show = original as (content: unknown, options?: unknown) => unknown;
      try {
        target[type] = (content: unknown, options?: unknown) =>
          isDuplicateOfAccessNotice(content) ? SUPPRESSED_TOAST_ID : show(content, options);
        originals[type] = original;
      } catch {
        /* a read-only export: duplicates simply show */
      }
    });
    return () => {
      Object.keys(originals).forEach((type) => {
        try {
          target[type] = originals[type];
        } catch {
          /* ignore */
        }
      });
    };
  }, []);
}

export default function ApiAccessNotice() {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const access = useApiAccessState();
  const secondsLeft = useRateLimitCountdown();
  const [checking, setChecking] = useState(false);
  const [checkNote, setCheckNote] = useState<string | null>(null);
  useDuplicateToastFilter();

  const dialogOpen = access.ipBlocked && access.ipBlockedNoticeOpen;
  const rateLimitOpen = access.rateLimitNoticeOpen && secondsLeft > 0;

  // A dialog opened again never shows the previous round's note.
  useEffect(() => {
    if (dialogOpen) setCheckNote(null);
  }, [dialogOpen]);

  // "Try again": exactly ONE request, never a loop. Only another 403 IP_BLOCKED keeps the dialog.
  const recheck = async () => {
    setChecking(true);
    setCheckNote(null);
    try {
      await apiFetch('Authenticate/validate-token', { method: 'GET' });
      clearIpBlocked();
    } catch (error) {
      if (isIpBlockedError(error)) {
        setCheckNote('Access is still blocked. Please contact support if you believe this is a mistake.');
      } else if (apiErrorStatus(error) === 0) {
        setCheckNote('Could not reach the server. Check the connection and try again.');
      } else {
        // Any other answer (a 401 on the sign-in page included): the API let this request through.
        clearIpBlocked();
      }
    } finally {
      setChecking(false);
    }
  };

  return (
    <>
      <Snackbar
        open={rateLimitOpen}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ maxWidth: { sm: 520 } }}
      >
        <Alert
          severity="warning"
          variant="filled"
          onClose={dismissRateLimitNotice}
          sx={{ width: '100%', boxShadow: 6, alignItems: 'flex-start' }}
        >
          <AlertTitle sx={{ fontWeight: 700 }}>Too Many Requests</AlertTitle>
          You have made too many requests. Please wait a moment and try again.
          {access.rateLimitDetail ? <Box sx={{ mt: 0.75 }}>{access.rateLimitDetail}</Box> : null}
          {access.rateLimitDelayKnown ? (
            <Box sx={{ mt: 0.75, fontWeight: 700 }}>Try again in {formatWait(secondsLeft)}</Box>
          ) : null}
        </Alert>
      </Snackbar>

      <Dialog
        open={dialogOpen}
        onClose={(_, reason) => {
          // Only the buttons (or Escape) close it: a stray click outside must not hide it unread.
          if (reason === 'backdropClick' || checking) return;
          dismissIpBlockedNotice();
        }}
        fullScreen={fullScreen}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Fade}
        transitionDuration={220}
        aria-labelledby="api-access-denied-title"
        aria-describedby="api-access-denied-text"
        PaperProps={{
          sx: {
            borderRadius: fullScreen ? 0 : 3,
            overflow: 'hidden',
            bgcolor: 'background.paper',
            backgroundImage: 'none',
            boxShadow: `0 24px 64px ${alpha(theme.palette.common.black, 0.28)}`
          }
        }}
      >
        <Box
          sx={{
            px: 3,
            pt: 4,
            pb: 3,
            textAlign: 'center',
            color: theme.palette.error.contrastText,
            background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              mx: 'auto',
              mb: 1.5,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.common.white, 0.18),
              border: `1px solid ${alpha(theme.palette.common.white, 0.28)}`
            }}
          >
            <GppBadTwoToneIcon sx={{ fontSize: 38 }} />
          </Box>
          <Typography id="api-access-denied-title" variant="h4" sx={{ fontWeight: 700, color: 'inherit' }}>
            Access Denied
          </Typography>
        </Box>

        <Box sx={{ px: 3, pt: 3, pb: 3 }}>
          <Typography id="api-access-denied-text" variant="body1" sx={{ color: 'text.secondary', textAlign: 'center' }}>
            Access from your IP address has been blocked. Please contact support if you believe this is a mistake.
          </Typography>
          {access.ipBlockedDetail ? (
            <Typography variant="body2" sx={{ mt: 1.5, color: 'text.primary', textAlign: 'center', fontWeight: 600 }}>
              {access.ipBlockedDetail}
            </Typography>
          ) : null}
          {checkNote ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              {checkNote}
            </Alert>
          ) : null}

          <Stack spacing={1.25} sx={{ mt: 3 }}>
            <Button
              fullWidth
              size="large"
              variant="contained"
              onClick={recheck}
              disabled={checking}
              startIcon={checking ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{ borderRadius: 2, py: 1.25, fontWeight: 700, textTransform: 'none', fontSize: 15 }}
            >
              {checking ? 'Checking…' : 'Try again'}
            </Button>
            <Button
              fullWidth
              size="large"
              variant="text"
              onClick={dismissIpBlockedNotice}
              disabled={checking}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
            >
              Close
            </Button>
          </Stack>

          {access.requestId ? (
            <Typography variant="caption" sx={{ display: 'block', mt: 2, textAlign: 'center', color: 'text.disabled' }}>
              Reference: {access.requestId}
            </Typography>
          ) : null}
        </Box>
      </Dialog>
    </>
  );
}
